import {
  LayoutDashboard,
  Target,
  ShieldCheck,
  Pickaxe,
  HardHat,
  AlertTriangle,
  Banknote,
  BarChart3,
  Users,
  Cog,
  ScrollText,
  Settings,
  Building2,
  Activity,
  Shield,
  ToggleRight,
  ClipboardCheck,
  ClipboardList,
  Leaf,
  Package,
  Wallet,
  FileText,
  Download,
  LifeBuoy,
  Network,
  Hourglass,
  UserCheck,
  Mountain,
  FlaskConical,
  Factory,
  Ruler,
  ShoppingCart,
  Truck,
  Code2,
  type LucideIcon,
} from 'lucide-react';
import { EUserRole, type Portal } from '@/types';
import { FeatureFlag, type EFeatureFlag } from '@/lib/feature-flags/keys';
import { flagsForPath } from '@/lib/feature-flags/module-routes';
import { FUEL_VIEW_PERMISSIONS, PERMISSIONS } from '@/lib/permissions';
import { translateHierarchyTerms, type HierarchyTermLabels } from '@/lib/hierarchy-terms';

export interface NavItem {
  label: string;
  href: string;
  icon?: LucideIcon;
  badge?: number;
  roles?: EUserRole[];
  featureFlag?: EFeatureFlag;
  /**
   * Permission required to see this item. Sub-items inherit the parent's
   * permission unless they declare their own. Omit for cross-cutting items
   * (Dashboard/Settings/Profile) that should follow role only.
   */
  permission?: string;
  /**
   * Hides the item from someone who lacks this permission, without offering it
   * to someone whose role menu does not list it. `permission` does both.
   */
  denyWithout?: string;
  /** Any one of these permissions shows the item, like `RequirePermission anyOf`
   * on its route. Takes the place of `permission`. */
  anyOf?: string[];
  /**
   * Which sidebar group this item sits under. Must be one of the group names
   * `SIDEBAR_GROUPS` declares for the portal. Omit only for the handful of
   * items that belong above the groups — Dashboard today. Held on the item
   * rather than derived from `label` or `href`, because tenant terminology
   * rewrites labels at runtime and several portals reuse one href across two
   * entries.
   */
  group?: string;
  children?: NavSubItem[];
}

export interface NavSubItem {
  label: string;
  href: string;
  roles?: EUserRole[];
  badge?: number;
  featureFlag?: EFeatureFlag;
  permission?: string;
  denyWithout?: string;
  anyOf?: string[];
  // Optional second level — when present the sub-item renders as a small
  // collapsible group instead of a direct link. The `href` then acts as a
  // fallback target (clicking the group label can navigate to it) but the
  // primary affordance is the expand/collapse chevron.
  children?: NavSubItem[];
}

// The compensation rules answer to either key, as the backend's own check does.
const COMPENSATION_VIEW_PERMISSIONS = [PERMISSIONS.COMPENSATION_RULE_VIEW, PERMISSIONS.PRICING_RULE_READ];

// The grade sample list answers to any of these, as the backend's own check does.
const GRADE_SAMPLE_VIEW_PERMISSIONS = [
  PERMISSIONS.GRADE_SAMPLE_VIEW,
  PERMISSIONS.GEOLOGY_VIEW,
  PERMISSIONS.LIMS_VIEW,
  PERMISSIONS.OPERATIONS_VIEW,
];

// The tailings page reads cells, generations and lots; any one of them opens it.
const TAILINGS_VIEW_PERMISSIONS = [PERMISSIONS.TSF_CELL_VIEW, PERMISSIONS.TAILINGS_VIEW, PERMISSIONS.LOT_VIEW];

// The traceability dashboard reads two analytics; either one opens it.
const TRACEABILITY_VIEW_PERMISSIONS = [
  PERMISSIONS.OPERATIONS_ANALYTICS_VIEW_MEU_TRACEABILITY,
  PERMISSIONS.OPERATIONS_ANALYTICS_VIEW_CUSTODY_INTEGRITY,
];

// The integrity pages read the hash chain; any one of its views opens them.
const INTEGRITY_VIEW_PERMISSIONS = [
  PERMISSIONS.HASH_CHAIN_VIEW_STATS,
  PERMISSIONS.HASH_CHAIN_VIEW_BLOCKS,
  PERMISSIONS.HASH_CHAIN_VIEW_HEAD_DETAIL,
];

// Each inventory page has its own view permission; the heading opens for any of them.
const INVENTORY_VIEW_PERMISSIONS = [
  PERMISSIONS.INVENTORY_EQUIPMENT_VIEW,
  PERMISSIONS.INVENTORY_MATERIAL_VIEW,
  PERMISSIONS.INVENTORY_STOCK_MOVEMENT_VIEW,
  PERMISSIONS.STOCK_COUNT_VIEW,
  PERMISSIONS.INVENTORY_ORE_STOCKPILE_VIEW,
  PERMISSIONS.MATERIAL_REQUISITION_VIEW,
  PERMISSIONS.MATERIAL_RECONCILIATION_VIEW,
];

const DIR = EUserRole.MINE_DIRECTOR;
const MGR = EUserRole.MINE_MANAGER;
const ITA = EUserRole.IT_ADMINISTRATOR;
// The Mining Clerk shares the IT Administrator's menu everywhere except the
// Administration group. Inside that group it keeps only the universal items
// (My Profile, Notification Preferences, Help Center) and Compensation Rules,
// whose permissions it holds. Anything else under Administration reaches a
// clerk only when an IT admin grants its permission in the matrix, which
// `getNavItemsForUser` then surfaces without this list being touched.
const CLK = EUserRole.MINING_CLERK;

export const adminSidebarConfig: NavItem[] = [
  {
    label: 'Dashboard',
    href: '/admin/dashboard',
    icon: LayoutDashboard,
    roles: [DIR, MGR, ITA, CLK],
    permission: PERMISSIONS.DASHBOARD_READ,
  },
  {
    // §6.3 custom KPI builder. Gated on the manage permission, not the view
    // one: a page whose only purpose is defining KPIs is no use to someone
    // who may only read them.
    label: 'Custom KPIs',
    group: 'Insights',
    href: '/admin/dashboard/kpis',
    icon: Target,
    roles: [DIR, MGR],
    permission: PERMISSIONS.KPI_TARGET_MANAGE,
  },
  {
    label: 'Pending Actions',
    group: 'Insights',
    href: '/admin/pending-actions',
    icon: Hourglass,
    roles: [DIR, MGR],
    featureFlag: FeatureFlag.PENDING_ACTIONS_VIEW,
    permission: PERMISSIONS.PENDING_ACTIONS_VIEW,
  },
  {
    label: 'Organisation Chart',
    group: 'Insights',
    href: '/admin/organisation',
    icon: Network,
    roles: [DIR, MGR, ITA, CLK],
    permission: PERMISSIONS.LEADERSHIP_HIERARCHY_READ,
  },
  {
    // Tenant-facing usage analytics. Mine Directors and IT Administrators were
    // granted `analytics.view` against a surface that had no UI at all.
    label: 'Usage & Adoption',
    group: 'Insights',
    href: '/admin/insights/usage',
    icon: BarChart3,
    roles: [DIR, ITA, CLK],
    permission: PERMISSIONS.ANALYTICS_VIEW,
  },
  {
    label: 'Critical Alerts',
    group: 'Insights',
    href: '/admin/alerts',
    icon: AlertTriangle,
    roles: [DIR, MGR, ITA, CLK],
    permission: PERMISSIONS.ALERT_VIEW,
  },
  {
    label: 'Compliance & Licensing',
    group: 'Compliance',
    href: '/admin/compliance',
    icon: ShieldCheck,
    roles: [DIR, MGR, ITA, CLK],
    permission: PERMISSIONS.COMPLIANCE_VIEW,
    children: [
      { label: 'License Register', href: '/admin/compliance/licenses', roles: [DIR, MGR], denyWithout: PERMISSIONS.COMPLIANCE_VIEW },
      { label: 'License Renewals', href: '/admin/compliance/licenses/renewals', roles: [DIR, MGR], denyWithout: PERMISSIONS.COMPLIANCE_VIEW },
      { label: 'Compliance Tracking', href: '/admin/compliance/requirements', roles: [DIR, MGR], denyWithout: PERMISSIONS.COMPLIANCE_VIEW },
      { label: 'Compliance Calendar', href: '/admin/compliance/calendar', roles: [DIR, MGR], denyWithout: PERMISSIONS.COMPLIANCE_VIEW },
      { label: 'Permit Alerts', href: '/admin/compliance/alerts', roles: [DIR, MGR], denyWithout: PERMISSIONS.COMPLIANCE_VIEW },
      { label: 'Commitment Tracker', href: '/admin/compliance/commitments', roles: [DIR, MGR], denyWithout: PERMISSIONS.COMPLIANCE_VIEW },
      { label: 'Document Repository', href: '/admin/compliance/documents', roles: [DIR, MGR, ITA, CLK], denyWithout: PERMISSIONS.COMPLIANCE_VIEW },
      { label: 'Compliance Reports', href: '/admin/compliance/reports', roles: [DIR, MGR], denyWithout: PERMISSIONS.COMPLIANCE_VIEW },
    ],
  },
  {
    label: 'Mine Operations',
    group: 'Operations',
    href: '/admin/operations',
    icon: Pickaxe,
    roles: [DIR, MGR, ITA, CLK],
    permission: PERMISSIONS.OPERATIONS_VIEW,
    children: [
      { label: 'Site Hierarchy', href: '/admin/operations/hierarchy', roles: [DIR, MGR, ITA, CLK], denyWithout: PERMISSIONS.OPERATIONS_VIEW },
      {
        label: 'Working Places',
        href: '/admin/operations/working-places',
        roles: [DIR, MGR, ITA, CLK],
        featureFlag: FeatureFlag.WORKING_PLACES_REGISTER,
        permission: PERMISSIONS.WORKINGPLACE_VIEW,
      },
      { label: 'Working Days', href: '/admin/operations/working-days', roles: [DIR, MGR, ITA, CLK], denyWithout: PERMISSIONS.WORKING_DAY_VIEW },
      { label: 'Development Tracker', href: '/admin/operations/development', roles: [DIR, MGR], denyWithout: PERMISSIONS.DEVELOPMENT_SHIFT_VIEW },
      {
        label: 'Planning Metrics',
        href: '/admin/operations/planning/metrics',
        roles: [DIR, MGR, ITA, CLK],
        featureFlag: FeatureFlag.MINE_PLANNING,
        permission: PERMISSIONS.PLANNINGMETRICS_VIEW,
      },
      {
        label: 'Code Lists',
        href: '/admin/operations/planning/code-lists',
        roles: [DIR, MGR, ITA, CLK],
        featureFlag: FeatureFlag.MINE_PLANNING,
        permission: PERMISSIONS.PLANNINGCODES_VIEW,
      },
      {
        label: 'Plans',
        href: '/admin/operations/planning/plans',
        roles: [DIR, MGR, ITA, CLK],
        featureFlag: FeatureFlag.MINE_PLANNING,
        permission: PERMISSIONS.PLAN_VERSION_VIEW,
      },
      {
        label: 'Daily Targets',
        href: '/admin/operations/planning/daily-targets',
        roles: [DIR, MGR, ITA, CLK],
        featureFlag: FeatureFlag.MINE_PLANNING,
        permission: PERMISSIONS.PLAN_DAILY_TARGET_VIEW,
      },
      {
        label: 'Operating Calendar',
        href: '/admin/operations/planning/calendar',
        roles: [DIR, MGR, ITA, CLK],
        featureFlag: FeatureFlag.MINE_PLANNING,
        permission: PERMISSIONS.PLANNINGPROFILE_VIEW,
      },
      { label: 'Blaster Certificates', href: '/admin/operations/blaster-certificates', roles: [DIR, MGR, ITA, CLK], featureFlag: FeatureFlag.BLASTING_MODULE, denyWithout: PERMISSIONS.BLASTER_CERTIFICATE_VIEW },
      { label: 'Equipment Logs', href: '/admin/operations/equipment-logs', roles: [DIR, MGR, ITA, CLK], featureFlag: FeatureFlag.STOCK_MODULE, denyWithout: PERMISSIONS.EQUIPMENT_LOG_VIEW },
      { label: 'Material Availability', href: '/admin/operations/material-availability', roles: [DIR, MGR, ITA, CLK], featureFlag: FeatureFlag.STOCK_MODULE, denyWithout: PERMISSIONS.MATERIAL_AVAILABILITY_VIEW },
      { label: 'Production Logs', href: '/admin/operations/production/logs', roles: [DIR, MGR], denyWithout: PERMISSIONS.PRODUCTION_LOG_VIEW },
      { label: 'Production Targets', href: '/admin/operations/production/targets', roles: [DIR, MGR], denyWithout: PERMISSIONS.PRODUCTION_TARGET_VIEW },
      { label: 'Weekly Production', href: '/admin/operations/production/weekly-summary', roles: [DIR, MGR], denyWithout: PERMISSIONS.OPERATIONS_REPORT_VIEW },
      { label: 'Traceability Dashboard', href: '/admin/operations/traceability', roles: [DIR, MGR], anyOf: TRACEABILITY_VIEW_PERMISSIONS },
      { label: 'Shift Logs', href: '/admin/operations/shift-logs', roles: [DIR, MGR], denyWithout: PERMISSIONS.SHIFT_LOG_VIEW },
      {
        label: 'Shifts',
        href: '/admin/operations/shifts',
        roles: [DIR, MGR, ITA, CLK],
        featureFlag: FeatureFlag.SHIFT_MANAGEMENT,
        permission: PERMISSIONS.SHIFT_VIEW,
      },
      {
        label: 'Shift Reports',
        href: '/admin/operations/shift-reports',
        roles: [DIR, MGR, ITA, CLK],
        featureFlag: FeatureFlag.SHIFT_MANAGEMENT,
        permission: PERMISSIONS.SHIFT_REPORT_VIEW,
      },
      {
        label: 'Daily Approval',
        href: '/admin/operations/daily-reports',
        roles: [DIR, MGR, ITA, CLK],
        featureFlag: FeatureFlag.MINE_PLANNING,
        permission: PERMISSIONS.DAILYREPORT_VIEW,
      },
      {
        label: 'Delay Analysis',
        href: '/admin/operations/delay-analysis',
        roles: [DIR, MGR, ITA, CLK],
        featureFlag: FeatureFlag.SHIFT_MANAGEMENT,
        permission: PERMISSIONS.DELAY_EVENT_VIEW,
      },
      {
        label: 'Plan vs Actual',
        href: '/admin/operations/variance',
        roles: [DIR, MGR, ITA, CLK],
        featureFlag: FeatureFlag.MINE_PLANNING,
        permission: PERMISSIONS.VARIANCE_VIEW,
      },
      {
        label: 'Variance Thresholds',
        href: '/admin/operations/variance-thresholds',
        roles: [DIR, MGR, ITA, CLK],
        featureFlag: FeatureFlag.MINE_PLANNING,
        permission: PERMISSIONS.VARIANCE_VIEW,
      },
      {
        label: 'Exceptions',
        href: '/admin/operations/exceptions',
        roles: [DIR, MGR],
        featureFlag: FeatureFlag.MINE_PLANNING,
        permission: PERMISSIONS.ALERT_EVENT_VIEW,
      },
      {
        label: 'Alert Rules',
        href: '/admin/operations/alert-rules',
        roles: [DIR, MGR],
        featureFlag: FeatureFlag.MINE_PLANNING,
        permission: PERMISSIONS.ALERT_RULE_VIEW,
      },
      {
        label: 'Fuel Issues',
        href: '/admin/operations/fuel-issues',
        roles: [DIR, MGR, ITA, CLK],
        anyOf: FUEL_VIEW_PERMISSIONS,
      },
      {
        label: 'Tank Dips',
        href: '/admin/operations/fuel-tank-dips',
        roles: [DIR, MGR, ITA, CLK],
        anyOf: FUEL_VIEW_PERMISSIONS,
      },
      { label: 'Bag Tracking / MEU', href: '/admin/operations/bags', roles: [DIR, MGR], denyWithout: PERMISSIONS.MEU_VIEW_LIST },
      { label: 'Lot Management', href: '/admin/operations/lots', roles: [DIR, MGR], denyWithout: PERMISSIONS.LOT_VIEW },
      { label: 'Tailings Tracking', href: '/admin/operations/tailings', roles: [DIR, MGR], anyOf: TAILINGS_VIEW_PERMISSIONS },
      // FR-CRYPTO-030 §6.9.4 — Integrity group: hash-chain verification +
      // per-tunnel signed report, grouped under one collapsible parent so
      // the Mine Operations menu doesn't carry two adjacent integrity slots.
      {
        label: 'Integrity',
        href: '/admin/operations/integrity',
        roles: [DIR, MGR, ITA, CLK],
        children: [
          { label: 'Integrity Verification', href: '/admin/operations/integrity', roles: [DIR, MGR, ITA, CLK], anyOf: INTEGRITY_VIEW_PERMISSIONS },
          { label: 'Integrity Report', href: '/admin/operations/integrity-report', roles: [DIR, MGR, ITA, CLK], denyWithout: PERMISSIONS.INTEGRITY_REPORT_VIEW },
        ],
      },
      { label: 'Hierarchy Naming', href: '/admin/operations/hierarchy-naming', roles: [ITA, CLK], denyWithout: PERMISSIONS.OPERATIONS_VIEW },
    ],
  },
  // Appendix B.4 / §6.2 — Geology & Resource Management
  {
    label: 'Geology & Resources',
    group: 'Operations',
    href: '/admin/geology',
    icon: Mountain,
    roles: [DIR, MGR, ITA, CLK],
    permission: PERMISSIONS.GEOLOGY_VIEW,
    featureFlag: FeatureFlag.GEOLOGY_MODULE,
    children: [
      { label: 'Drill Hole Register', href: '/admin/geology/drill-holes', roles: [DIR, MGR, ITA, CLK], denyWithout: PERMISSIONS.GEOLOGY_VIEW },
      { label: 'Resource Blocks', href: '/admin/geology/blocks', roles: [DIR, MGR], denyWithout: PERMISSIONS.GEOLOGY_VIEW },
      { label: 'Resource Statements', href: '/admin/geology/statements', roles: [DIR, MGR], denyWithout: PERMISSIONS.GEOLOGY_VIEW },
      { label: 'Geological Mapping', href: '/admin/geology/mapping', roles: [DIR, MGR, ITA, CLK], denyWithout: PERMISSIONS.GEOLOGY_VIEW },
      { label: 'Grade Samples', href: '/admin/geology/grade-samples', roles: [DIR, MGR, ITA, CLK], anyOf: GRADE_SAMPLE_VIEW_PERMISSIONS },
    ],
  },
  // Appendix B.4 / §6.5 — Laboratory. The `lims.view` permission on this parent
  // is the confidentiality control: a role without it never sees the section.
  {
    label: 'Laboratory',
    group: 'Operations',
    href: '/admin/laboratory',
    icon: FlaskConical,
    roles: [DIR, MGR, ITA, CLK],
    permission: PERMISSIONS.LIMS_VIEW,
    featureFlag: FeatureFlag.LIMS_MODULE,
    children: [
      { label: 'Samples', href: '/admin/laboratory/samples', roles: [DIR, MGR, ITA, CLK], denyWithout: PERMISSIONS.LIMS_VIEW },
      { label: 'Batch QAQC', href: '/admin/laboratory/qaqc', roles: [DIR, MGR, ITA, CLK], denyWithout: PERMISSIONS.LIMS_VIEW },
      { label: 'Grade Certificates', href: '/admin/laboratory/certificates', roles: [DIR, MGR, ITA, CLK], denyWithout: PERMISSIONS.LIMS_VIEW },
      { label: 'Import Results', href: '/admin/laboratory/import', roles: [DIR, MGR], permission: PERMISSIONS.LIMS_CREATE },
    ],
  },
  // Appendix B.4 / §6.1 — Ore Processing
  {
    label: 'Ore Processing',
    group: 'Operations',
    href: '/admin/processing',
    icon: Factory,
    roles: [DIR, MGR, ITA, CLK],
    permission: PERMISSIONS.ORE_PROCESSING_VIEW,
    featureFlag: FeatureFlag.ORE_PROCESSING_MODULE,
    children: [
      { label: 'Plants', href: '/admin/processing/plants', roles: [DIR, MGR, ITA, CLK], denyWithout: PERMISSIONS.ORE_PROCESSING_VIEW },
      { label: 'Batches', href: '/admin/processing/batches', roles: [DIR, MGR, ITA, CLK], denyWithout: PERMISSIONS.ORE_PROCESSING_VIEW },
    ],
  },
  // Appendix B.4 / §6.6 — Surveying & Mapping
  {
    label: 'Surveying',
    group: 'Operations',
    href: '/admin/surveying',
    icon: Ruler,
    roles: [DIR, MGR, ITA, CLK],
    permission: PERMISSIONS.SURVEYING_VIEW,
    featureFlag: FeatureFlag.SURVEYING_MODULE,
    children: [
      { label: 'Survey Register', href: '/admin/surveying/surveys', roles: [DIR, MGR, ITA, CLK], denyWithout: PERMISSIONS.SURVEYING_VIEW },
      { label: 'Reconciliation', href: '/admin/surveying/reconciliation', roles: [DIR, MGR], denyWithout: PERMISSIONS.SURVEYING_VIEW },
      { label: 'Coordinate System', href: '/admin/surveying/coordinate-system', roles: [DIR, MGR, ITA, CLK], denyWithout: PERMISSIONS.SURVEYING_VIEW },
    ],
  },
  // Appendix B.4 / §6.4 — Procurement & Vendors
  {
    label: 'Procurement',
    group: 'Commercial',
    href: '/admin/procurement',
    icon: ShoppingCart,
    roles: [DIR, MGR, ITA, CLK],
    permission: PERMISSIONS.PROCUREMENT_VIEW,
    featureFlag: FeatureFlag.PROCUREMENT_MODULE,
    children: [
      { label: 'Vendors', href: '/admin/procurement/vendors', roles: [DIR, MGR, ITA, CLK], denyWithout: PERMISSIONS.PROCUREMENT_VIEW },
      { label: 'Purchase Orders', href: '/admin/procurement/purchase-orders', roles: [DIR, MGR, ITA, CLK], denyWithout: PERMISSIONS.PROCUREMENT_VIEW },
      { label: 'Approval Bands', href: '/admin/procurement/approval-bands', roles: [DIR, MGR, ITA, CLK], denyWithout: PERMISSIONS.PROCUREMENT_VIEW },
      { label: 'Spend Analytics', href: '/admin/procurement/spend', roles: [DIR, MGR], permission: PERMISSIONS.PROCUREMENT_EXPORT },
    ],
  },
  // Appendix B.4 / §6.7 — Supply Chain & Logistics
  {
    label: 'Logistics',
    group: 'Commercial',
    href: '/admin/logistics',
    icon: Truck,
    roles: [DIR, MGR, ITA, CLK],
    permission: PERMISSIONS.LOGISTICS_VIEW,
    featureFlag: FeatureFlag.LOGISTICS_MODULE,
    children: [
      { label: 'Dispatch Board', href: '/admin/logistics/dispatches', roles: [DIR, MGR, ITA, CLK], denyWithout: PERMISSIONS.LOGISTICS_VIEW },
      { label: 'Carriers', href: '/admin/logistics/carriers', roles: [DIR, MGR, ITA, CLK], denyWithout: PERMISSIONS.LOGISTICS_VIEW },
      { label: 'Discrepancies', href: '/admin/logistics/discrepancies', roles: [DIR, MGR], denyWithout: PERMISSIONS.LOGISTICS_VIEW },
    ],
  },
  {
    label: 'Workforce',
    group: 'People & Safety',
    href: '/admin/workforce',
    icon: HardHat,
    roles: [DIR, MGR, ITA, CLK],
    permission: PERMISSIONS.WORKFORCE_VIEW,
    children: [
      { label: 'Workers', href: '/admin/workforce/workers', roles: [DIR, MGR, ITA, CLK], denyWithout: PERMISSIONS.WORKER_VIEW },
      // FR-LBR-003 / §15.2 — Mine Manager queue for approving workers
      // registered by a Subcontractor under the "Subcontractor register,
      // Manager approve" workflow.
      { label: 'Worker Approvals', href: '/admin/workforce/worker-approvals', roles: [DIR, MGR], denyWithout: PERMISSIONS.WORKER_VIEW },
      // Its own permission: a holder of `attendance.view` gets the page even
      // without `workforce.view`, and `workforce.view` alone does not.
      { label: 'Attendance', href: '/admin/workforce/attendance', roles: [DIR, MGR], permission: PERMISSIONS.ATTENDANCE_VIEW },
      { label: 'Labour Tracking', href: '/admin/workforce/labour-tracking', roles: [DIR, MGR], denyWithout: PERMISSIONS.TEAM_LABOUR_LOG_VIEW },
      { label: 'Compensation Config', href: '/admin/workforce/compensation', roles: [MGR, ITA, CLK], anyOf: COMPENSATION_VIEW_PERMISSIONS },
      { label: 'Subcontractors', href: '/admin/workforce/subcontractors', roles: [DIR, MGR], featureFlag: FeatureFlag.SUBCONTRACTOR_MODULE, denyWithout: PERMISSIONS.SUBCONTRACTOR_VIEW },
    ],
  },
  {
    label: 'Safety & Environment',
    group: 'People & Safety',
    href: '/admin/safety',
    icon: AlertTriangle,
    roles: [DIR, MGR, ITA, CLK],
    permission: PERMISSIONS.SAFETY_VIEW,
    children: [
      { label: 'Safety Dashboard', href: '/admin/safety/dashboard', roles: [DIR, MGR], denyWithout: PERMISSIONS.SAFETY_VIEW },
      { label: 'Incidents', href: '/admin/safety/incidents', roles: [DIR, MGR, ITA, CLK], denyWithout: PERMISSIONS.SAFETY_VIEW },
      { label: 'Incident Type Configuration', href: '/admin/safety/incident-types', roles: [DIR, MGR, ITA, CLK], denyWithout: PERMISSIONS.SAFETY_VIEW },
      { label: 'Inspection Type Configuration', href: '/admin/safety/inspection-types', roles: [DIR, MGR, ITA, CLK], denyWithout: PERMISSIONS.SAFETY_VIEW },
      { label: 'Inspection Checklist Templates', href: '/admin/safety/inspection-checklists', roles: [DIR, MGR, ITA, CLK], denyWithout: PERMISSIONS.SAFETY_VIEW },
      { label: 'Risk Thresholds', href: '/admin/safety/risk-thresholds', roles: [DIR, MGR, ITA, CLK], denyWithout: PERMISSIONS.SAFETY_VIEW },
      { label: 'Hazard / Risk Register', href: '/admin/safety/hazards', roles: [DIR, MGR], denyWithout: PERMISSIONS.SAFETY_VIEW },
      { label: 'Inspections', href: '/admin/safety/inspections', roles: [DIR, MGR], denyWithout: PERMISSIONS.SAFETY_VIEW },
      { label: 'Corrective Actions', href: '/admin/safety/corrective-actions', roles: [DIR, MGR], denyWithout: PERMISSIONS.SAFETY_VIEW },
      { label: 'Safety Equipment', href: '/admin/safety/equipment', roles: [DIR, MGR], featureFlag: FeatureFlag.STOCK_MODULE, permission: PERMISSIONS.INVENTORY_EQUIPMENT_VIEW },
      { label: 'Environmental Monitoring', href: '/admin/safety/environment', roles: [DIR, MGR], featureFlag: FeatureFlag.ENVIRONMENTAL_MODULE, denyWithout: PERMISSIONS.ENVIRONMENT_VIEW },
      { label: 'Environmental Incidents', href: '/admin/safety/environment/incidents', roles: [DIR, MGR], featureFlag: FeatureFlag.ENVIRONMENTAL_MODULE, denyWithout: PERMISSIONS.ENVIRONMENT_VIEW },
    ],
  },
  {
    label: 'Visitors',
    group: 'People & Safety',
    href: '/admin/visitors/register',
    icon: UserCheck,
    roles: [DIR, MGR, ITA, CLK],
    permission: PERMISSIONS.VISITOR_VIEW,
    featureFlag: FeatureFlag.VISITOR_MANAGEMENT_MODULE,
    children: [
      { label: 'Visitors Register', href: '/admin/visitors/register', roles: [DIR, MGR, ITA, CLK], denyWithout: PERMISSIONS.VISITOR_VIEW },
      { label: 'Pending Approvals', href: '/admin/visitors/approvals', roles: [DIR, MGR], permission: PERMISSIONS.VISITOR_APPROVE },
    ],
  },
  {
    label: 'Inventory',
    group: 'Commercial',
    href: '/admin/inventory',
    icon: Package,
    roles: [MGR],
    featureFlag: FeatureFlag.STOCK_MODULE,
    anyOf: INVENTORY_VIEW_PERMISSIONS,
    children: [
      { label: 'Equipment Availability', href: '/admin/inventory/equipment/availability', roles: [DIR, MGR], permission: PERMISSIONS.INVENTORY_EQUIPMENT_VIEW },
      { label: 'Material Requisitions', href: '/admin/inventory/requisitions', roles: [MGR], permission: PERMISSIONS.MATERIAL_REQUISITION_VIEW },
    ],
  },
  {
    label: 'Finance & Payments',
    group: 'Commercial',
    href: '/admin/finance',
    icon: Banknote,
    roles: [DIR, MGR, ITA, CLK],
    permission: PERMISSIONS.FINANCE_VIEW,
    children: [
      // Payment Configuration is switched off for every user.
      // { label: 'Payment Config', href: '/admin/finance/config', roles: [ITA, CLK, MGR], denyWithout: PERMISSIONS.FINANCE_VIEW },
      { label: 'Payment Batches', href: '/admin/finance/batches', roles: [DIR, MGR], permission: PERMISSIONS.PAYMENT_BATCH_VIEW },
      { label: 'Payment History', href: '/admin/finance/history', roles: [DIR], permission: PERMISSIONS.PAYMENT_HISTORY_VIEW },
      { label: 'Reconciliation', href: '/admin/finance/reconciliation', roles: [DIR], permission: PERMISSIONS.PAYMENT_RECONCILIATION_VIEW },
      {
        label: 'Financial Requisitions',
        href: '/admin/finance/requisitions',
        roles: [DIR, MGR, ITA, CLK],
        featureFlag: FeatureFlag.FINANCIAL_REQUISITIONS,
        permission: PERMISSIONS.FINANCIAL_REQUISITION_VIEW,
      },
      {
        label: 'Payment Runs',
        href: '/admin/finance/payment-runs',
        roles: [DIR, MGR],
        featureFlag: FeatureFlag.PAYMENT_RUNS,
        permission: PERMISSIONS.PAYMENT_RUN_VIEW,
      },
    ],
  },
  {
    // Unified Reports workspace — Generate, Builder, Generated Reports,
    // Scheduled, and Weekly are tabs within the page (each deep-linkable).
    label: 'Reports & Analytics',
    group: 'Insights',
    href: '/admin/reports',
    icon: BarChart3,
    roles: [DIR, MGR, ITA, CLK],
    permission: PERMISSIONS.REPORTS_VIEW,
  },
  {
    label: 'User Management',
    group: 'Administration',
    href: '/admin/users',
    icon: Users,
    roles: [ITA],
    permission: PERMISSIONS.USER_READ,
    children: [
      { label: 'Users', href: '/admin/users/list', roles: [ITA], permission: PERMISSIONS.USER_READ },
      { label: 'Departments', href: '/admin/users/departments', roles: [ITA], permission: PERMISSIONS.DEPARTMENT_READ },
      { label: 'Roles & Permissions', href: '/admin/users/roles', roles: [ITA], permission: PERMISSIONS.ROLE_READ },
    ],
  },
  {
    label: 'Tenant Configuration',
    group: 'Administration',
    href: '/admin/config',
    icon: Cog,
    roles: [ITA],
    permission: PERMISSIONS.CONFIG_VIEW,
    children: [
      { label: 'Onboarding Checklist', href: '/admin/config/onboarding', roles: [ITA], permission: PERMISSIONS.CONFIG_VIEW },
      { label: 'Feature Flags', href: '/admin/config/features', roles: [ITA], permission: PERMISSIONS.CONFIG_VIEW },
      { label: 'Financial Year', href: '/admin/config/financial-year', roles: [ITA], permission: PERMISSIONS.CONFIG_VIEW, featureFlag: FeatureFlag.MINE_PLANNING },
      {
        label: 'Daily Activity Lists',
        href: '/admin/daily-activity/lists',
        roles: [ITA],
        featureFlag: FeatureFlag.DAILY_ACTIVITY_REPORTS,
        denyWithout: PERMISSIONS.DAILY_ACTIVITY_CODE_LIST_VIEW,
      },
    ],
  },
  {
    label: 'Audit Log',
    group: 'Compliance',
    href: '/admin/audit-log',
    icon: ScrollText,
    roles: [ITA, CLK, DIR],
    permission: PERMISSIONS.AUDIT_VIEW,
  },
  {
    label: 'KYB Verification',
    group: 'Compliance',
    href: '/admin/kyb',
    icon: ShieldCheck,
    roles: [ITA, CLK],
    // Tenant identity verification, not compliance casework: it reads and
    // submits the tenant's own KYB documents, so it is gated with the rest of
    // the tenant-profile family. The Mining Clerk does not hold `tenant.view`.
    permission: PERMISSIONS.TENANT_VIEW,
  },
  {
    // FR14 — Support Tickets
    label: 'Support',
    group: 'Administration',
    href: '/admin/support/my-tickets',
    icon: LifeBuoy,
    roles: [ITA, CLK, DIR, MGR],
    children: [
      { label: 'My Tickets', href: '/admin/support/my-tickets', roles: [ITA, CLK, DIR, MGR], permission: PERMISSIONS.MY_SUPPORT_TICKET_VIEW },
      {
        label: 'Ticket Queue',
        href: '/admin/support/queue',
        roles: [ITA, DIR, MGR],
        permission: PERMISSIONS.SUPPORT_TICKET_VIEW,
      },
    ],
  },
  // The developer platform is the IT Administrator's own surface: it is where a
  // tenant obtains the API key the SDK is constructed with. Gated on the
  // DEVELOPER_API flag, which ships disabled.
  {
    label: 'Developer',
    group: 'Administration',
    href: '/admin/developer',
    icon: Code2,
    roles: [ITA, DIR],
    permission: PERMISSIONS.APIKEY_VIEW,
    featureFlag: FeatureFlag.DEVELOPER_API,
    children: [
      { label: 'API Keys', href: '/admin/developer/api-keys', roles: [ITA, DIR], permission: PERMISSIONS.APIKEY_VIEW },
      { label: 'Webhooks', href: '/admin/developer/webhooks', roles: [ITA, DIR], permission: PERMISSIONS.WEBHOOK_VIEW },
      { label: 'Deliveries', href: '/admin/developer/deliveries', roles: [ITA, DIR], permission: PERMISSIONS.WEBHOOK_VIEW },
      { label: 'IP Allowlist', href: '/admin/developer/ip-allowlist', roles: [ITA], permission: PERMISSIONS.IP_ALLOWLIST_VIEW },
    ],
  },
  {
    label: 'Settings',
    group: 'Administration',
    href: '/admin/settings',
    icon: Settings,
    roles: [ITA, CLK, DIR, MGR],
    children: [
      { label: 'Tenant Profile', href: '/admin/settings/profile', roles: [ITA], permission: PERMISSIONS.TENANT_VIEW },
      { label: 'Branding', href: '/admin/settings/branding', roles: [ITA], permission: PERMISSIONS.SETTINGS_WRITE },
      { label: 'Custom Domain', href: '/admin/settings/custom-domain', roles: [MGR, ITA], permission: PERMISSIONS.CONFIG_CREATE },
      // FR2 item 20 — org-wide notification channel ceiling/floor.
      { label: 'Notification Policy', href: '/admin/settings/notification-policy', roles: [ITA], permission: PERMISSIONS.SETTINGS_WRITE },
      { label: 'Workflow Config', href: '/admin/config/workflows', roles: [ITA], permission: PERMISSIONS.CONFIG_VIEW },
      // FR-TRACE-014 — who signs off a custody transfer, and how many
      // signatures it takes. Signing itself is mobile-only.
      {
        label: 'Custody Approval',
        href: '/admin/config/custody-approval',
        roles: [ITA],
        permission: PERMISSIONS.CUSTODY_APPROVAL_CONFIGURE,
        featureFlag: FeatureFlag.CUSTODY_TRANSFER_APPROVAL,
      },
      // CE-01 — Compensation Rules surfaces under Settings/Tenant Configuration
      // as well, accessible to Mine Director / Manager (full access) plus
      // IT Administrator (config).
      { label: 'Compensation Rules', href: '/admin/workforce/compensation', roles: [DIR, MGR, ITA, CLK], permission: PERMISSIONS.COMPENSATION_RULE_VIEW },
      { label: 'My Profile', href: '/admin/settings/my-profile', roles: [DIR, MGR, ITA, CLK] },
      {
        label: 'Notification Preferences',
        href: '/admin/config/notifications',
        roles: [DIR, MGR, ITA, CLK],
        permission: PERMISSIONS.NOTIFICATION_PREFERENCE_VIEW,
      },
      { label: 'Help Center', href: '/help-center', roles: [DIR, MGR, ITA, CLK] },
    ],
  },
];

// Field Operations Portal Sidebar

const ENG = EUserRole.MINE_ENGINEER;
const ZNE = EUserRole.ZONE_ENGINEER;
const SSE = EUserRole.SENIOR_SAFETY_ENGINEER;
const JSE = EUserRole.JUNIOR_SAFETY_ENGINEER;
const ENV = EUserRole.ENVIRONMENTALIST;
const STK = EUserRole.STOCK_MANAGER;
const FIN = EUserRole.FINANCE_OFFICER;
const FSV = EUserRole.FIELD_SUPERVISOR;
const ALL_OPS = [ENG, SSE, JSE, ENV, STK, FIN, FSV];

export const opsSidebarConfig: NavItem[] = [
  {
    label: 'Dashboard',
    href: '/admin/dashboard',
    icon: LayoutDashboard,
    roles: ALL_OPS,
  },
  {
    label: 'Pending Actions',
    group: 'Insights',
    href: '/admin/pending-actions',
    icon: Hourglass,
    roles: [ENG, ZNE, SSE, JSE, STK, FSV],
    featureFlag: FeatureFlag.PENDING_ACTIONS_VIEW,
    permission: PERMISSIONS.PENDING_ACTIONS_VIEW,
  },
  {
    label: 'Organisation Chart',
    group: 'Insights',
    href: '/admin/organisation',
    icon: Network,
    roles: ALL_OPS,
    permission: PERMISSIONS.LEADERSHIP_HIERARCHY_READ,
  },
  {
    label: 'Production & Traceability',
    group: 'Operations',
    href: '/admin/operations',
    icon: Pickaxe,
    roles: [ENG, FSV, STK, SSE],
    permission: PERMISSIONS.OPERATIONS_VIEW,
    // Grouped by the work it belongs to rather than listed flat. Twenty-one
    // entries was too many to scan, and the four roles sharing this menu each
    // see only part of it. Sub-groups follow the same pattern as the admin
    // portal's "Integrity" group: the group's href is its first child's, and
    // its roles are the union of its children's, so a role that can see any
    // child can open the group. `getVisibleNavItems` drops a group whose
    // children all filter out, so none of these can render empty.
    children: [
      {
        label: 'Shifts',
        href: '/admin/operations/shift-logs',
        roles: [ENG, FSV],
        children: [
          { label: 'Shift Logs', href: '/admin/operations/shift-logs', roles: [ENG, FSV], denyWithout: PERMISSIONS.SHIFT_LOG_VIEW },
          {
            label: 'Shifts',
            href: '/admin/operations/shifts',
            roles: [ENG, FSV],
            featureFlag: FeatureFlag.SHIFT_MANAGEMENT,
            permission: PERMISSIONS.SHIFT_VIEW,
          },
          {
            label: 'Shift Reports',
            href: '/admin/operations/shift-reports',
            roles: [ENG, FSV],
            featureFlag: FeatureFlag.SHIFT_MANAGEMENT,
            permission: PERMISSIONS.SHIFT_REPORT_VIEW,
          },
          {
            label: 'Delay Analysis',
            href: '/admin/operations/delay-analysis',
            roles: [ENG, FSV],
            featureFlag: FeatureFlag.SHIFT_MANAGEMENT,
            permission: PERMISSIONS.DELAY_EVENT_VIEW,
          },
        ],
      },
      {
        label: 'Production',
        href: '/admin/operations/production',
        roles: [ENG, FSV],
        children: [
          { label: 'Production Overview', href: '/admin/operations/production', roles: [ENG], denyWithout: PERMISSIONS.SHIFT_LOG_VIEW },
          // FR-OPS §6.6 — production tracking for field crews
          { label: 'Production Logs', href: '/admin/operations/production/logs', roles: [ENG, FSV], denyWithout: PERMISSIONS.PRODUCTION_LOG_VIEW },
          { label: 'Weekly Production', href: '/admin/operations/production/weekly-summary', roles: [ENG], denyWithout: PERMISSIONS.OPERATIONS_REPORT_VIEW },
          { label: 'Development Tracker', href: '/admin/operations/development', roles: [ENG, FSV], denyWithout: PERMISSIONS.DEVELOPMENT_SHIFT_VIEW },
        ],
      },
      {
        label: 'Traceability',
        href: '/admin/operations/bags',
        roles: [ENG, FSV, STK],
        children: [
          { label: 'Bag Tracking / MEU', href: '/admin/operations/bags', roles: [ENG, FSV, STK], denyWithout: PERMISSIONS.MEU_VIEW_LIST },
          { label: 'Lot Management', href: '/admin/operations/lots', roles: [ENG, STK], denyWithout: PERMISSIONS.LOT_VIEW },
          { label: 'Integrity Verification', href: '/admin/operations/integrity', roles: [ENG], anyOf: INTEGRITY_VIEW_PERMISSIONS },
        ],
      },
      {
        label: 'Planning & Setup',
        href: '/admin/operations/planning/plans',
        roles: [ENG, FSV],
        children: [
          { label: 'Plans', href: '/admin/operations/planning/plans', roles: [ENG], featureFlag: FeatureFlag.MINE_PLANNING, permission: PERMISSIONS.PLAN_VERSION_VIEW },
          { label: 'Working Days', href: '/admin/operations/working-days', roles: [ENG, FSV], denyWithout: PERMISSIONS.WORKING_DAY_VIEW },
          { label: 'Site Hierarchy', href: '/admin/operations/hierarchy', roles: [ENG], denyWithout: PERMISSIONS.OPERATIONS_VIEW },
        ],
      },
      {
        label: 'Equipment & Fuel',
        href: '/admin/operations/equipment-logs',
        roles: [ENG, FSV, STK],
        children: [
          // FR-EQUIP §6.5 — equipment + material entries for field crews
          { label: 'Equipment Logs', href: '/admin/operations/equipment-logs', roles: [ENG, FSV, STK], featureFlag: FeatureFlag.STOCK_MODULE, denyWithout: PERMISSIONS.EQUIPMENT_LOG_VIEW },
          { label: 'Material Availability', href: '/admin/operations/material-availability', roles: [ENG, FSV, STK], featureFlag: FeatureFlag.STOCK_MODULE, denyWithout: PERMISSIONS.MATERIAL_AVAILABILITY_VIEW },
          {
            label: 'Fuel Issues',
            href: '/admin/operations/fuel-issues',
            roles: [ENG, FSV, STK],
            anyOf: FUEL_VIEW_PERMISSIONS,
          },
          {
            label: 'Tank Dips',
            href: '/admin/operations/fuel-tank-dips',
            roles: [ENG, FSV, STK],
            anyOf: FUEL_VIEW_PERMISSIONS,
          },
        ],
      },
      {
        // Named for the module rather than repeating the child's label: a
        // group called Blasting whose first item is also Blasting reads as a
        // mistake. Drill & Blast is the industry term and matches the
        // backend's drill-and-blast controller.
        label: 'Drill & Blast',
        href: '/admin/operations/blasting',
        roles: [ENG, SSE, JSE],
        children: [
          { label: 'Blasting', href: '/admin/operations/blasting', roles: [ENG, SSE, JSE], featureFlag: FeatureFlag.BLASTING_MODULE, denyWithout: PERMISSIONS.SHIFT_LOG_VIEW },
          { label: 'Blaster Certificates', href: '/admin/operations/blaster-certificates', roles: [ENG, SSE], featureFlag: FeatureFlag.BLASTING_MODULE, denyWithout: PERMISSIONS.BLASTER_CERTIFICATE_VIEW },
        ],
      },
      // Left flat deliberately: one page, so a group would add a click and
      // tell the reader nothing they did not already know from the label.
      { label: 'Tailings Tracking', href: '/admin/operations/tailings', roles: [ENG], anyOf: TAILINGS_VIEW_PERMISSIONS },
    ],
  },
  // Appendix B.4 / §6.2 — the geologist's own surfaces. The Mine Engineer holds
  // the geology grants; no other operational role does.
  {
    label: 'Geology & Resources',
    group: 'Operations',
    href: '/admin/geology',
    icon: Mountain,
    roles: [ENG],
    permission: PERMISSIONS.GEOLOGY_VIEW,
    featureFlag: FeatureFlag.GEOLOGY_MODULE,
    children: [
      { label: 'Drill Hole Register', href: '/admin/geology/drill-holes', roles: [ENG], denyWithout: PERMISSIONS.GEOLOGY_VIEW },
      { label: 'Resource Blocks', href: '/admin/geology/blocks', roles: [ENG], denyWithout: PERMISSIONS.GEOLOGY_VIEW },
      { label: 'Resource Statements', href: '/admin/geology/statements', roles: [ENG], denyWithout: PERMISSIONS.GEOLOGY_VIEW },
      { label: 'Geological Mapping', href: '/admin/geology/mapping', roles: [ENG], denyWithout: PERMISSIONS.GEOLOGY_VIEW },
      { label: 'Grade Samples', href: '/admin/geology/grade-samples', roles: [ENG], anyOf: GRADE_SAMPLE_VIEW_PERMISSIONS },
    ],
  },
  // §6.5 — the Mine Engineer is the only operational role holding lims grants.
  // Finance, stock and field-supervision roles are deliberately absent.
  {
    label: 'Laboratory',
    group: 'Operations',
    href: '/admin/laboratory',
    icon: FlaskConical,
    roles: [ENG],
    permission: PERMISSIONS.LIMS_VIEW,
    featureFlag: FeatureFlag.LIMS_MODULE,
    children: [
      { label: 'Samples', href: '/admin/laboratory/samples', roles: [ENG], denyWithout: PERMISSIONS.LIMS_VIEW },
      { label: 'Batch QAQC', href: '/admin/laboratory/qaqc', roles: [ENG], denyWithout: PERMISSIONS.LIMS_VIEW },
      { label: 'Grade Certificates', href: '/admin/laboratory/certificates', roles: [ENG], denyWithout: PERMISSIONS.LIMS_VIEW },
      { label: 'Import Results', href: '/admin/laboratory/import', roles: [ENG], permission: PERMISSIONS.LIMS_CREATE },
    ],
  },
  {
    label: 'Ore Processing',
    group: 'Operations',
    href: '/admin/processing',
    icon: Factory,
    roles: [ENG, STK],
    permission: PERMISSIONS.ORE_PROCESSING_VIEW,
    featureFlag: FeatureFlag.ORE_PROCESSING_MODULE,
    children: [
      { label: 'Plants', href: '/admin/processing/plants', roles: [ENG], denyWithout: PERMISSIONS.ORE_PROCESSING_VIEW },
      { label: 'Batches', href: '/admin/processing/batches', roles: [ENG, STK], denyWithout: PERMISSIONS.ORE_PROCESSING_VIEW },
    ],
  },
  {
    label: 'Surveying',
    group: 'Operations',
    href: '/admin/surveying',
    icon: Ruler,
    roles: [ENG],
    permission: PERMISSIONS.SURVEYING_VIEW,
    featureFlag: FeatureFlag.SURVEYING_MODULE,
    children: [
      { label: 'Survey Register', href: '/admin/surveying/surveys', roles: [ENG], denyWithout: PERMISSIONS.SURVEYING_VIEW },
      { label: 'Reconciliation', href: '/admin/surveying/reconciliation', roles: [ENG], denyWithout: PERMISSIONS.SURVEYING_VIEW },
      { label: 'Coordinate System', href: '/admin/surveying/coordinate-system', roles: [ENG], denyWithout: PERMISSIONS.SURVEYING_VIEW },
    ],
  },
  // §6.4 — the Stock Manager runs the receiving counter; the Finance Officer
  // holds view and export only, which is why spend analytics is theirs and the
  // order surfaces are not.
  {
    label: 'Procurement',
    group: 'Commercial',
    href: '/admin/procurement',
    icon: ShoppingCart,
    roles: [STK, FIN],
    permission: PERMISSIONS.PROCUREMENT_VIEW,
    featureFlag: FeatureFlag.PROCUREMENT_MODULE,
    children: [
      { label: 'Vendors', href: '/admin/procurement/vendors', roles: [STK, FIN], denyWithout: PERMISSIONS.PROCUREMENT_VIEW },
      { label: 'Purchase Orders', href: '/admin/procurement/purchase-orders', roles: [STK, FIN], denyWithout: PERMISSIONS.PROCUREMENT_VIEW },
      { label: 'Spend Analytics', href: '/admin/procurement/spend', roles: [FIN], permission: PERMISSIONS.PROCUREMENT_EXPORT },
    ],
  },
  // §6.7 — mineral transport and its custody chain is what a Government
  // Inspector looks at, which is why that role holds `logistics.view`.
  {
    label: 'Logistics',
    group: 'Commercial',
    href: '/admin/logistics',
    icon: Truck,
    roles: [STK, ENG],
    permission: PERMISSIONS.LOGISTICS_VIEW,
    featureFlag: FeatureFlag.LOGISTICS_MODULE,
    children: [
      { label: 'Dispatch Board', href: '/admin/logistics/dispatches', roles: [STK, ENG], denyWithout: PERMISSIONS.LOGISTICS_VIEW },
      { label: 'Carriers', href: '/admin/logistics/carriers', roles: [STK], denyWithout: PERMISSIONS.LOGISTICS_VIEW },
      { label: 'Discrepancies', href: '/admin/logistics/discrepancies', roles: [STK], denyWithout: PERMISSIONS.LOGISTICS_VIEW },
    ],
  },
  {
    label: 'Attendance & Workforce',
    group: 'People & Safety',
    href: '/admin/workforce',
    icon: ClipboardCheck,
    roles: [FSV, ENG, SSE, JSE],
    permission: PERMISSIONS.WORKFORCE_VIEW,
    children: [
      { label: 'Attendance', href: '/admin/workforce/attendance', roles: [FSV, ENG], permission: PERMISSIONS.ATTENDANCE_VIEW },
      { label: 'Labour Tracking', href: '/admin/workforce/labour-tracking', roles: [FSV, ENG], denyWithout: PERMISSIONS.TEAM_LABOUR_LOG_VIEW },
      { label: 'Worker Directory', href: '/admin/workforce/workers', roles: [FSV, ENG, SSE, JSE], denyWithout: PERMISSIONS.WORKER_VIEW },
    ],
  },
  {
    label: 'Safety & Inspections',
    group: 'People & Safety',
    href: '/admin/safety',
    icon: AlertTriangle,
    // FR-SAF-001 — every tenant employee can reach incident reporting via the
    // Incidents page's "Report Incident" action.
    roles: ALL_OPS,
    permission: PERMISSIONS.SAFETY_VIEW,
    children: [
      { label: 'Incidents', href: '/admin/safety/incidents', roles: ALL_OPS, denyWithout: PERMISSIONS.SAFETY_VIEW },
      { label: 'Inspections', href: '/admin/safety/inspections', roles: [SSE, JSE, ENV], denyWithout: PERMISSIONS.SAFETY_VIEW },
      { label: 'Hazard Register', href: '/admin/safety/hazards', roles: [SSE, JSE, FSV], denyWithout: PERMISSIONS.SAFETY_VIEW },
      { label: 'Corrective Actions', href: '/admin/safety/corrective-actions', roles: ALL_OPS, denyWithout: PERMISSIONS.SAFETY_VIEW },
      { label: 'Safety Equipment', href: '/admin/safety/equipment', roles: [SSE, JSE, ENV], featureFlag: FeatureFlag.STOCK_MODULE, permission: PERMISSIONS.INVENTORY_EQUIPMENT_VIEW },
    ],
  },
  {
    label: 'Daily Activity',
    group: 'People & Safety',
    href: '/admin/daily-activity/activity-counters',
    icon: ClipboardList,
    roles: [SSE, JSE, ENV],
    featureFlag: FeatureFlag.DAILY_ACTIVITY_REPORTS,
    permission: PERMISSIONS.DAILYACTIVITY_VIEW,
    children: [
      {
        label: 'Activity Counters',
        href: '/admin/daily-activity/activity-counters',
        roles: [SSE, JSE, ENV],
        denyWithout: PERMISSIONS.DAILYACTIVITY_VIEW,
      },
      {
        label: 'Sanitation Round',
        href: '/admin/daily-activity/sanitation-round',
        roles: [SSE, JSE, ENV],
        denyWithout: PERMISSIONS.DAILYACTIVITY_VIEW,
      },
      {
        label: 'Waste Status',
        href: '/admin/daily-activity/waste-status',
        roles: [SSE, JSE, ENV],
        denyWithout: PERMISSIONS.DAILYACTIVITY_VIEW,
      },
      {
        label: 'Lists',
        href: '/admin/daily-activity/lists',
        roles: [SSE],
        denyWithout: PERMISSIONS.DAILY_ACTIVITY_CODE_LIST_VIEW,
      },
    ],
  },
  {
    // Raising a request for money is a Commercial/Finance matter. Field
    // Supervisor and both Safety Engineer tiers already have a Commercial
    // group (via Inventory) but no nested Finance section, so this is its
    // own top-level entry inside that same group rather than a child of
    // Inventory.
    label: 'Financial Requisitions',
    group: 'Commercial',
    href: '/admin/finance/requisitions',
    icon: Wallet,
    roles: [FSV, SSE, JSE],
    featureFlag: FeatureFlag.FINANCIAL_REQUISITIONS,
    permission: PERMISSIONS.FINANCIAL_REQUISITION_VIEW,
  },
  {
    label: 'Visitors',
    group: 'People & Safety',
    href: '/admin/visitors/new',
    icon: UserCheck,
    roles: [SSE, JSE],
    permission: PERMISSIONS.VISITOR_CREATE,
    featureFlag: FeatureFlag.VISITOR_MANAGEMENT_MODULE,
    children: [
      { label: 'New Induction Form', href: '/admin/visitors/new', roles: [SSE, JSE], denyWithout: PERMISSIONS.VISITOR_CREATE },
      { label: 'Visitors Register', href: '/admin/visitors/register', roles: [SSE, JSE], permission: PERMISSIONS.VISITOR_VIEW },
    ],
  },
  {
    label: 'Environment',
    group: 'People & Safety',
    href: '/admin/safety/environment',
    icon: Leaf,
    roles: [ENV],
    featureFlag: FeatureFlag.ENVIRONMENTAL_MODULE,
    permission: PERMISSIONS.ENVIRONMENT_VIEW,
    children: [
      { label: 'Environmental Monitoring', href: '/admin/safety/environment', roles: [ENV], denyWithout: PERMISSIONS.ENVIRONMENT_VIEW },
      { label: 'Environmental Incidents', href: '/admin/safety/environment/incidents', roles: [ENV], denyWithout: PERMISSIONS.ENVIRONMENT_VIEW },
      { label: 'Environmental Compliance', href: '/admin/safety/environment/compliance', roles: [ENV], denyWithout: PERMISSIONS.ENVIRONMENT_VIEW },
      { label: 'Tailings Tracking', href: '/admin/operations/tailings', roles: [ENV], anyOf: TAILINGS_VIEW_PERMISSIONS },
    ],
  },
  {
    label: 'Inventory',
    group: 'Commercial',
    href: '/admin/inventory',
    icon: Package,
    roles: [STK, ENG, FSV, MGR],
    featureFlag: FeatureFlag.STOCK_MODULE,
    anyOf: INVENTORY_VIEW_PERMISSIONS,
    children: [
      { label: 'Materials & Equipment', href: '/admin/inventory/materials', roles: [STK], anyOf: [PERMISSIONS.INVENTORY_MATERIAL_VIEW, PERMISSIONS.INVENTORY_EQUIPMENT_VIEW] },
      { label: 'Categories', href: '/admin/inventory/categories', roles: [STK], anyOf: [PERMISSIONS.INVENTORY_MATERIAL_CATEGORY_VIEW, PERMISSIONS.INVENTORY_EQUIPMENT_TYPE_VIEW] },
      { label: 'Stock Movements', href: '/admin/inventory/movements', roles: [STK], permission: PERMISSIONS.INVENTORY_STOCK_MOVEMENT_VIEW },
      { label: 'Stock Counts', href: '/admin/inventory/counts', roles: [STK], permission: PERMISSIONS.STOCK_COUNT_VIEW },
      { label: 'Ore Stockpile', href: '/admin/inventory/stockpile', roles: [STK], permission: PERMISSIONS.INVENTORY_ORE_STOCKPILE_VIEW },
      { label: 'Material Requisitions', href: '/admin/inventory/requisitions', roles: [STK, ENG, FSV, MGR], permission: PERMISSIONS.MATERIAL_REQUISITION_VIEW },
      { label: 'Material Reconciliation', href: '/admin/inventory/reconciliation', roles: [STK], permission: PERMISSIONS.MATERIAL_RECONCILIATION_VIEW },
    ],
  },
  {
    label: 'Workforce',
    group: 'People & Safety',
    href: '/admin/workforce',
    icon: Users,
    roles: [FIN],
    permission: PERMISSIONS.WORKFORCE_VIEW,
    children: [
      { label: 'Workers', href: '/admin/workforce/workers', roles: [FIN], denyWithout: PERMISSIONS.WORKER_VIEW },
    ],
  },
  {
    label: 'Finance & Payroll',
    group: 'Commercial',
    href: '/admin/finance',
    icon: Wallet,
    roles: [FIN],
    permission: PERMISSIONS.FINANCE_VIEW,
    children: [
      { label: 'Payment Batches', href: '/admin/finance/batches', roles: [FIN], permission: PERMISSIONS.PAYMENT_BATCH_VIEW },
      { label: 'Payment History', href: '/admin/finance/history', roles: [FIN], permission: PERMISSIONS.PAYMENT_HISTORY_VIEW },
      { label: 'Reconciliation', href: '/admin/finance/reconciliation', roles: [FIN], permission: PERMISSIONS.PAYMENT_RECONCILIATION_VIEW },
      // CE-01 — Finance Officers need to read/preview compensation rules
      // (e.g. before approving payment batches).
      { label: 'Compensation Rules', href: '/admin/workforce/compensation', roles: [FIN], permission: PERMISSIONS.COMPENSATION_RULE_VIEW },
      {
        label: 'Financial Requisitions',
        href: '/admin/finance/requisitions',
        roles: [FIN],
        featureFlag: FeatureFlag.FINANCIAL_REQUISITIONS,
        permission: PERMISSIONS.FINANCIAL_REQUISITION_VIEW,
      },
      {
        label: 'Payment Runs',
        href: '/admin/finance/payment-runs',
        roles: [FIN],
        featureFlag: FeatureFlag.PAYMENT_RUNS,
        permission: PERMISSIONS.PAYMENT_RUN_VIEW,
      },
    ],
  },
  {
    // Unified Reports workspace — tabs (Generate / Builder / Generated /
    // Scheduled / Weekly) gate themselves by role inside the page.
    label: 'Reports',
    group: 'Insights',
    href: '/admin/reports',
    icon: FileText,
    roles: ALL_OPS,
    permission: PERMISSIONS.REPORTS_VIEW,
  },
  {
    label: 'Settings',
    group: 'Administration',
    href: '/admin/settings',
    icon: Settings,
    roles: ALL_OPS,
    children: [
      { label: 'My Profile', href: '/admin/settings/my-profile', roles: ALL_OPS },
      {
        label: 'Notification Preferences',
        href: '/admin/config/notifications',
        roles: ALL_OPS,
        permission: PERMISSIONS.NOTIFICATION_PREFERENCE_VIEW,
      },
      { label: 'Help Center', href: '/help-center', roles: ALL_OPS },
    ],
  },
];

// External Portal Sidebar

const SUB = EUserRole.SUBCONTRACTOR;
const GOV = EUserRole.GOVERNMENT_INSPECTOR;

export const externalSidebarConfig: NavItem[] = [
  {
    label: 'Dashboard',
    href: '/admin/dashboard',
    icon: LayoutDashboard,
    roles: [SUB, GOV],
  },
  {
    label: 'Organisation Chart',
    group: 'Insights',
    href: '/admin/organisation',
    icon: Network,
    roles: [SUB, GOV],
    permission: PERMISSIONS.LEADERSHIP_HIERARCHY_READ,
  },
  {
    label: 'My Workers',
    group: 'People & Safety',
    href: '/admin/workforce',
    icon: Users,
    roles: [SUB],
    featureFlag: FeatureFlag.SUBCONTRACTOR_MODULE,
    permission: PERMISSIONS.WORKFORCE_VIEW,
    children: [
      { label: 'Worker Registry', href: '/admin/workforce/workers', roles: [SUB], denyWithout: PERMISSIONS.WORKER_VIEW },
      { label: 'Register Worker', href: '/admin/workforce/workers/new', roles: [SUB], denyWithout: PERMISSIONS.WORKFORCE_CREATE },
      { label: 'Attendance', href: '/admin/workforce/attendance', roles: [SUB], permission: PERMISSIONS.ATTENDANCE_VIEW },
    ],
  },
  {
    // §4.2.10 — Subcontractor can upload their own compliance documents
    // (company licence, insurance, etc.). Read-and-upload only, scoped to
    // their own ownerId by the backend.
    label: 'My Compliance',
    group: 'Compliance',
    href: '/admin/compliance',
    icon: ShieldCheck,
    roles: [SUB],
    featureFlag: FeatureFlag.SUBCONTRACTOR_MODULE,
    permission: PERMISSIONS.COMPLIANCE_VIEW,
    children: [
      { label: 'My Documents', href: '/admin/compliance/documents', roles: [SUB], denyWithout: PERMISSIONS.COMPLIANCE_VIEW },
    ],
  },
  {
    label: 'Zone Production',
    group: 'Operations',
    href: '/admin/operations',
    icon: Pickaxe,
    roles: [SUB],
    permission: PERMISSIONS.OPERATIONS_VIEW,
    children: [
      { label: 'Production Overview', href: '/admin/operations/production', roles: [SUB], denyWithout: PERMISSIONS.SHIFT_LOG_VIEW },
      { label: 'Bag Tracking', href: '/admin/operations/bags', roles: [SUB], denyWithout: PERMISSIONS.MEU_VIEW_LIST },
    ],
  },
  {
    // SRS §4.2.10 + FR-LBR-052 — Subcontractors must be able to ACKNOWLEDGE
    // payment batches that contain their workers (the final step of the
    // batch workflow). The page enforces SUB-specific gating internally:
    // no Create/Approve/Submit actions, acknowledge-only.
    label: 'Payments',
    group: 'Commercial',
    href: '/admin/finance',
    icon: Wallet,
    roles: [SUB],
    permission: PERMISSIONS.FINANCE_VIEW,
    children: [
      { label: 'Payment Batches', href: '/admin/finance/batches', roles: [SUB], permission: PERMISSIONS.PAYMENT_BATCH_VIEW },
      {
        label: 'Financial Requisitions',
        href: '/admin/finance/requisitions',
        roles: [SUB],
        featureFlag: FeatureFlag.FINANCIAL_REQUISITIONS,
        permission: PERMISSIONS.FINANCIAL_REQUISITION_VIEW,
      },
    ],
  },
  {
    label: 'Compliance Review',
    group: 'Compliance',
    href: '/admin/compliance',
    icon: ShieldCheck,
    roles: [GOV],
    permission: PERMISSIONS.COMPLIANCE_VIEW,
    children: [
      { label: 'License Register', href: '/admin/compliance/licenses', roles: [GOV], denyWithout: PERMISSIONS.COMPLIANCE_VIEW },
      { label: 'Compliance Tracking', href: '/admin/compliance/requirements', roles: [GOV], denyWithout: PERMISSIONS.COMPLIANCE_VIEW },
      { label: 'Compliance Calendar', href: '/admin/compliance/calendar', roles: [GOV], denyWithout: PERMISSIONS.COMPLIANCE_VIEW },
      { label: 'Document Repository', href: '/admin/compliance/documents', roles: [GOV], denyWithout: PERMISSIONS.COMPLIANCE_VIEW },
      { label: 'Inspection Feedback', href: '/admin/compliance/inspections', roles: [GOV], denyWithout: PERMISSIONS.COMPLIANCE_VIEW },
    ],
  },
  {
    label: 'Safety Review',
    group: 'People & Safety',
    href: '/admin/safety',
    icon: AlertTriangle,
    roles: [GOV],
    permission: PERMISSIONS.SAFETY_VIEW,
    children: [
      { label: 'Incidents', href: '/admin/safety/incidents', roles: [GOV], denyWithout: PERMISSIONS.SAFETY_VIEW },
      { label: 'Inspection Records', href: '/admin/safety/inspections', roles: [GOV], denyWithout: PERMISSIONS.SAFETY_VIEW },
      { label: 'Hazard Register', href: '/admin/safety/hazards', roles: [GOV], denyWithout: PERMISSIONS.SAFETY_VIEW },
      { label: 'Environmental Monitoring', href: '/admin/safety/environment', roles: [GOV], featureFlag: FeatureFlag.ENVIRONMENTAL_MODULE, denyWithout: PERMISSIONS.ENVIRONMENT_VIEW },
      { label: 'Environmental Incidents', href: '/admin/safety/environment/incidents', roles: [GOV], featureFlag: FeatureFlag.ENVIRONMENTAL_MODULE, denyWithout: PERMISSIONS.ENVIRONMENT_VIEW },
    ],
  },
  {
    label: 'Traceability',
    group: 'Operations',
    href: '/admin/operations',
    icon: Package,
    roles: [GOV],
    permission: PERMISSIONS.OPERATIONS_VIEW,
    children: [
      { label: 'Lot Management', href: '/admin/operations/lots', roles: [GOV], denyWithout: PERMISSIONS.LOT_VIEW },
      { label: 'Integrity Verification', href: '/admin/operations/integrity', roles: [GOV], anyOf: INTEGRITY_VIEW_PERMISSIONS },
    ],
  },
  {
    label: 'Workers',
    group: 'People & Safety',
    href: '/admin/workforce/workers',
    icon: HardHat,
    roles: [GOV],
    permission: PERMISSIONS.WORKER_VIEW,
  },
  {
    // FR-SAF-060/061 — inspectors generate/export safety (and compliance/
    // traceability) reports here. Unified Reports workspace; inspectors see the
    // Generate, Builder, and Generated Reports tabs (not Scheduled/Weekly).
    label: 'Reports',
    group: 'Insights',
    href: '/admin/reports',
    icon: Download,
    roles: [GOV],
    permission: PERMISSIONS.REPORTS_VIEW,
  },
  {
    label: 'Settings',
    group: 'Administration',
    href: '/admin/settings',
    icon: Settings,
    roles: [SUB, GOV],
    children: [
      { label: 'My Profile', href: '/admin/settings/my-profile', roles: [SUB, GOV] },
      {
        label: 'Notification Preferences',
        href: '/admin/config/notifications',
        roles: [SUB, GOV],
        permission: PERMISSIONS.NOTIFICATION_PREFERENCE_VIEW,
      },
      { label: 'Help Center', href: '/help-center', roles: [SUB, GOV] },
    ],
  },
];

// Super Admin Portal Sidebar

const SA = EUserRole.SUPER_ADMIN;

export const superAdminSidebarConfig: NavItem[] = [
  {
    label: 'Dashboard',
    href: '/admin/dashboard',
    icon: LayoutDashboard,
    roles: [SA],
  },
  {
    label: 'Usage Analytics',
    group: 'Tenants',
    href: '/admin/analytics/usage',
    icon: BarChart3,
    roles: [SA],
    // Gated like Platform Audit, which sits beside it in this portal. This entry
    // was role-only while its neighbour carried a permission, which made the page
    // impossible to delegate without a code change.
    permission: PERMISSIONS.ANALYTICS_VIEW,
  },
  {
    label: 'Tenants',
    group: 'Tenants',
    href: '/admin/tenants',
    icon: Building2,
    roles: [SA],
    children: [
      { label: 'All Tenants', href: '/admin/tenants', roles: [SA] },
      { label: 'Onboarding Queue', href: '/admin/tenants/onboarding', roles: [SA] },
      { label: 'Provision Tenant', href: '/admin/tenants/provision', roles: [SA] },
      { label: 'KYB Reviews', href: '/admin/kyb-reviews', roles: [SA], badge: 1 },
      { label: 'KYB Configuration', href: '/admin/kyb-config', roles: [SA] },
    ],
  },
  {
    label: 'User Management',
    group: 'Administration',
    href: '/admin/users/list',
    icon: Users,
    roles: [SA],
    children: [
      { label: 'Users', href: '/admin/users/list', roles: [SA], denyWithout: PERMISSIONS.USER_READ },
      { label: 'Departments', href: '/admin/users/departments', roles: [SA], denyWithout: PERMISSIONS.DEPARTMENT_READ },
      { label: 'Roles & Permissions', href: '/admin/users/roles', roles: [SA], denyWithout: PERMISSIONS.ROLE_READ },
    ],
  },
  {
    label: 'Feature Flags',
    group: 'Platform',
    href: '/admin/feature-flags',
    icon: ToggleRight,
    roles: [SA],
  },
  {
    label: 'System Health',
    group: 'Platform',
    href: '/admin/system-health',
    icon: Activity,
    roles: [SA],
    children: [
      { label: 'Service Status', href: '/admin/system-health', roles: [SA] },
      { label: 'Job Queues', href: '/admin/system-health/queues', roles: [SA] },
      { label: 'Error Logs', href: '/admin/system-health/errors', roles: [SA] },
    ],
  },
  {
    label: 'Security',
    group: 'Platform',
    href: '/admin/security',
    icon: Shield,
    roles: [SA],
    children: [
      { label: 'Security Event Feed', href: '/admin/security/events', roles: [SA] },
      { label: 'Failed Login Monitor', href: '/admin/security/failed-logins', roles: [SA] },
    ],
  },
  {
    label: 'Platform Audit',
    group: 'Platform',
    href: '/admin/platform-audit',
    icon: ScrollText,
    roles: [SA],
  },
  {
    label: 'Settings',
    group: 'Administration',
    href: '/admin/platform-settings',
    icon: Settings,
    roles: [SA],
    children: [
      { label: 'Platform Settings', href: '/admin/platform-settings', roles: [SA] },
      { label: 'Security Policies', href: '/admin/platform-settings/security', roles: [SA] },
      { label: 'KYB Requirements', href: '/admin/kyb-config', roles: [SA] },
    ],
  },
];

// Portal-based config selector

export type SidebarHierarchyLabels = HierarchyTermLabels;

function labelizeItem<T extends { label: string; children?: NavSubItem[] }>(
  item: T,
  labels: SidebarHierarchyLabels,
): T {
  const next: T = { ...item, label: translateHierarchyTerms(item.label, labels) };
  if (item.children) {
    next.children = item.children.map((c) => ({
      ...c,
      label: translateHierarchyTerms(c.label, labels),
    }));
  }
  return next;
}

export function getSidebarConfig(
  portal: Portal | null,
  labels?: SidebarHierarchyLabels,
): NavItem[] {
  let base: NavItem[];
  switch (portal) {
    case 'super-admin':
      base = superAdminSidebarConfig;
      break;
    case 'admin':
      base = adminSidebarConfig;
      break;
    case 'ops':
      base = opsSidebarConfig;
      break;
    case 'external':
      base = externalSidebarConfig;
      break;
    default:
      base = adminSidebarConfig;
  }
  if (!labels) return base;
  return base.map((g) => labelizeItem(g, labels));
}

interface VisibilityOptions {
  /** Show items the role's own menu does not list, because a permission opens them. */
  grantedByPermission?: boolean;
}

export function getVisibleNavItems(
  items: NavItem[],
  activeRole: EUserRole,
  flagEnabled?: (flag: EFeatureFlag) => boolean,
  can?: (permission: string) => boolean,
  options: VisibilityOptions = {},
): NavItem[] {
  const byPermission = options.grantedByPermission === true;
  if (byPermission && !can) return [];
  const flagOk = (flag?: EFeatureFlag) => !flag || !flagEnabled || flagEnabled(flag);
  // Permission-gating is additive on top of role + feature-flag filters.
  // `can` is optional so callers without permission context keep prior
  // (role-only) behaviour. An item carries either its own `permission` or
  // inherits the nearest ancestor's (`inherited`).
  const permOk = (permission?: string) => !permission || !can || can(permission);
  // A page is available only when the flags its address needs are on, the same
  // table the page itself is gated by.
  const routeFlagsOk = (href?: string) =>
    !href || !flagEnabled || flagsForPath(href).every(flagEnabled);
  const visible = (
    it: {
      href?: string;
      roles?: EUserRole[];
      featureFlag?: EFeatureFlag;
      permission?: string;
      denyWithout?: string;
      anyOf?: string[];
    },
    inherited?: string,
  ) => {
    const roleOk = byPermission
      ? Boolean(it.anyOf ?? it.permission)
      : !it.roles || it.roles.includes(activeRole);
    return (
      roleOk &&
      flagOk(it.featureFlag) &&
      routeFlagsOk(it.href) &&
      (it.anyOf
        ? it.anyOf.some(permOk)
        : permOk(it.permission ?? it.denyWithout ?? inherited))
    );
  };
  const parentVisible = (item: NavItem) =>
    byPermission && item.children?.length
      ? flagOk(item.featureFlag) && routeFlagsOk(item.href)
      : visible(item);

  function filterSubItem(
    child: NavSubItem,
    inherited?: string,
  ): NavSubItem | null {
    const effective = child.permission ?? inherited;
    if (!visible(child, inherited)) return null;
    if (!child.children) return child;
    const grandchildren = child.children
      .map((gc) => filterSubItem(gc, effective))
      .filter((c): c is NavSubItem => c != null);
    // If a sub-group declares children but none are visible to this role,
    // hide the group entirely rather than rendering an empty expander.
    if (grandchildren.length === 0) return null;
    return { ...child, children: grandchildren };
  }

  return items
    .filter(parentVisible)
    .flatMap((item) => {
      const filtered = item.children
        ?.map((child) => filterSubItem(child, item.permission))
        .filter((c): c is NavSubItem => c != null);
      // A parent that declared children but has none left is only a heading;
      // rendering it would turn it into a link to an address with no page.
      if (item.children?.length && filtered?.length === 0) return [];
      return [
        {
          ...item,
          ...(filtered ? { children: filtered } : {}),
        },
      ];
    });
}

function getGrantableNavItems(
  portal: Portal | null,
  labels?: SidebarHierarchyLabels,
): NavItem[] {
  if (portal === 'super-admin') return [];
  const configs = [adminSidebarConfig, opsSidebarConfig, externalSidebarConfig];
  return configs.flatMap((config) =>
    labels ? config.map((item) => labelizeItem(item, labels)) : config,
  );
}

function mergeGrantedNavItems(own: NavItem[], granted: NavItem[]): NavItem[] {
  const hrefsOf = (item: NavItem | NavSubItem): string[] => [
    item.href,
    ...(item.children ?? []).flatMap(hrefsOf),
  ];
  const seen = new Set(own.flatMap(hrefsOf));
  const merged = own.map((item) => ({ ...item }));

  for (const item of granted) {
    if (!item.children?.length) {
      if (seen.has(item.href)) continue;
      seen.add(item.href);
      merged.push(item);
      continue;
    }
    const fresh = item.children.filter((child) => !seen.has(child.href));
    if (fresh.length === 0) continue;
    fresh.forEach((child) => seen.add(child.href));
    // Two role menus can each carry a group of the same name with a different
    // landing page (Visitors), so a group is the same one by its label and
    // heading as well as by its link.
    const existing = merged.find(
      (candidate) =>
        candidate.href === item.href ||
        (candidate.label === item.label && candidate.group === item.group),
    );
    if (existing) existing.children = [...(existing.children ?? []), ...fresh];
    else merged.push({ ...item, children: fresh });
  }
  return merged;
}

/**
 * The menu for one person: their role's own entries, plus every page a granted
 * permission opens that the role's menu does not list, so a permission given in
 * the matrix reaches the sidebar instead of only the API.
 */
export function getNavItemsForUser(
  portal: Portal | null,
  labels: SidebarHierarchyLabels | undefined,
  activeRole: EUserRole,
  flagEnabled?: (flag: EFeatureFlag) => boolean,
  can?: (permission: string) => boolean,
): NavItem[] {
  const own = getVisibleNavItems(
    getSidebarConfig(portal, labels),
    activeRole,
    flagEnabled,
    can,
  );
  const granted = getVisibleNavItems(
    getGrantableNavItems(portal, labels),
    activeRole,
    flagEnabled,
    can,
    { grantedByPermission: true },
  );
  return mergeGrantedNavItems(own, granted);
}

// ── Sidebar groups ──────────────────────────────────────────────────────────
//
// The admin portal reached 24 top-level entries, which is more than anyone can
// scan. Items are bucketed into a handful of collapsible groups instead. The
// grouping is presentation only: `getVisibleNavItems` still decides what a
// role may see, and Header.tsx still consumes the flat list for search, so an
// item hidden by role, permission or feature flag is hidden here too.

export interface NavGroup {
  /** Undefined for the ungrouped block that renders above the groups. */
  label?: string;
  icon?: LucideIcon;
  items: NavItem[];
}

interface GroupSpec {
  label: string;
  icon: LucideIcon;
}

/**
 * Group order per portal, top to bottom. A group with no visible items is
 * dropped rather than rendered empty, so a role that cannot see any of
 * Operations never sees the heading either.
 */
const SIDEBAR_GROUPS: Record<Portal, GroupSpec[]> = {
  admin: [
    { label: 'Operations', icon: Pickaxe },
    { label: 'Commercial', icon: ShoppingCart },
    { label: 'People & Safety', icon: HardHat },
    { label: 'Compliance', icon: ShieldCheck },
    { label: 'Insights', icon: BarChart3 },
    { label: 'Administration', icon: Cog },
  ],
  ops: [
    { label: 'Operations', icon: Pickaxe },
    { label: 'Commercial', icon: ShoppingCart },
    { label: 'People & Safety', icon: HardHat },
    { label: 'Insights', icon: BarChart3 },
    { label: 'Administration', icon: Cog },
  ],
  external: [
    { label: 'Operations', icon: Pickaxe },
    { label: 'People & Safety', icon: HardHat },
    { label: 'Compliance', icon: ShieldCheck },
    { label: 'Commercial', icon: Wallet },
    { label: 'Insights', icon: BarChart3 },
    { label: 'Administration', icon: Cog },
  ],
  'super-admin': [
    { label: 'Tenants', icon: Building2 },
    { label: 'Platform', icon: Shield },
    { label: 'Administration', icon: Cog },
  ],
};

/** The group names a portal declares — used by the drift test. */
export function getSidebarGroupNames(portal: Portal): string[] {
  return SIDEBAR_GROUPS[portal].map((g) => g.label);
}

/**
 * Bucket already-filtered nav items into their groups. Items carrying no
 * `group` come back first, ungrouped, in their original order.
 */
export function getSidebarGroups(
  portal: Portal | null,
  items: NavItem[],
): NavGroup[] {
  const specs = SIDEBAR_GROUPS[portal ?? 'admin'];
  const ungrouped = items.filter((item) => !item.group);
  const groups = specs
    .map((spec) => ({
      label: spec.label,
      icon: spec.icon,
      items: items.filter((item) => item.group === spec.label),
    }))
    .filter((group) => group.items.length > 0);

  // An item whose group name is not declared for this portal would otherwise
  // vanish from the sidebar without a trace. Surface it rather than lose it.
  const placed = new Set(specs.map((s) => s.label));
  const orphans = items.filter((item) => item.group && !placed.has(item.group));

  return [
    ...(ungrouped.length || orphans.length
      ? [{ items: [...ungrouped, ...orphans] }]
      : []),
    ...groups,
  ];
}
